/**
 * Routes "Review" for procedure *node* copy / move / delete audits onto
 * `/audit-trail/reviewcopymovedelete/:id` so behaviour can diverge from add/edit/save
 * reviews (`/audit-trail/review/:id`) without touching those flows.
 */

function mergeAuditVersionContentFlat(vc) {
  if (!vc || typeof vc !== "object" || Array.isArray(vc)) return {};
  const inner = vc.version_content;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return { ...vc, ...inner };
  }
  return { ...vc };
}

/**
 * Same idea as {@link ViewAuditTrail}: MoveAction / preview-current-node audits —
 * not CREATE_/UPDATE_/SAVE_ add-or-edit rows (e.g. REVISION_COPIED stays on general review).
 *
 * Copy & replace across documents uses human `action_to_show` like
 * "User copied and replaced SUBASSEMBLY X into SUBASSEMBLY Y" (no SECTION_COPIED-style token).
 *
 * @param {object | null | undefined} revData - Audit row (get-by-id shape) or preview payload with version_content
 * @returns {boolean}
 */
export function isAuditTrailNodeChangeReviewEntry(revData) {
  if (!revData || typeof revData !== "object") return false;
  const vc = revData.version_content;
  if (!vc || typeof vc !== "object") return false;
  const flat = mergeAuditVersionContentFlat(vc);
  const atsRaw = String(flat.action_to_show || revData.action_to_show || "");
  const fullHint = [
    atsRaw,
    typeof revData.action === "string" ? revData.action : "",
    typeof revData.comment === "string" ? revData.comment : "",
    typeof flat.action === "string" ? flat.action : "",
  ]
    .filter((s) => s && String(s).trim())
    .join(" ");
  if (!fullHint.trim()) return false;

  /** Full procedure copy & replace (VersionHistoryCopy / CopyReplace) — route to bulk snapshot review. */
  if (
    /\bUSER\s+COPIED\s+AND\s+REPLACED\b/i.test(fullHint) ||
    /\bCOPIED\s+AND\s+REPLACED\b/i.test(fullHint)
  ) {
    return true;
  }

  const ats = atsRaw.toUpperCase();
  if (!ats.trim()) return false;
  /* Procedure-revision copy uses MoveAction UI but is not the same as section/step node ops. */
  if (ats.includes("REVISION_COPIED")) return false;
  return (
    ats.includes("_MOVED") ||
    ats.includes("_COPIED") ||
    ats.includes("_NODE_DELETED")
  );
}

/** @param {string | number} auditTrailId */
export function auditTrailNodeChangeReviewPath(auditTrailId) {
  return `/audit-trail/reviewcopymovedelete/${encodeURIComponent(String(auditTrailId))}`;
}

/** @param {string | number} auditTrailId */
export function auditTrailGeneralReviewPath(auditTrailId) {
  return `/audit-trail/review/${encodeURIComponent(String(auditTrailId))}`;
}
